import { css } from '@emotion/react';
import { classValidatorResolver } from '@hookform/resolvers/class-validator';
import { Flex, Text, Title } from '@inflearn/ds-react';
import { useLang } from '@inflearn/i18n-tools';
import { Trans, useTranslation } from 'next-i18next';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import {
  type CheckPhoneVerificationCodeResponse,
  ResponseStatus,
} from '../../../../../../resources/@types/accountSchema';
import { ApiError } from '../../../../../../resources/ApiError';
import { logger } from '../../../../../../resources/Logger';
import useClientAuthPhoneVerificationCheckCreateMutation from '../../../../../mutations/useClientAuthPhoneVerificationCheckCreateMutation';
import { PhoneNumberValidation } from '../../../../../validators/PhoneNumberValidation';
import { PhoneVerificationForm } from '../../../../common/PhoneVerificationForm';
import VerificationCodeInput, {
  type VerificationCodeInputProps,
} from '../../../../common/VerificationCodeInput';

export function PhoneVerify({
  onVerify,
}: {
  onVerify: (accounts: CheckPhoneVerificationCodeResponse[]) => void;
}) {
  const { t } = useTranslation();
  const { countryCode } = useLang();
  const [isSend, setIsSend] = useState(false);

  const form = useForm<PhoneNumberValidation>({
    mode: 'all',
    resolver: classValidatorResolver(PhoneNumberValidation),
    defaultValues: {
      countryCode,
    },
  });

  const verifyCheckMutation = useClientAuthPhoneVerificationCheckCreateMutation({
    onSuccess: (response) => {
      onVerify?.(response.data);
    },
    onError: (error) => {
      if (ApiError.isApiError(error) && error.statusCode === ResponseStatus.BAD_REQUEST) {
        return;
      }
      logger.error(error);
    },
  });

  const onClickCompletes: VerificationCodeInputProps['onClickCompletes'] = (data, options) => {
    verifyCheckMutation.mutate(
      {
        verifyCode: data.verifyCode,
        phone: data.phone,
        countryCode: data.countryCode,
      },
      options,
    );
  };

  return (
    <FormProvider {...form}>
      <Flex direction="column" w="100%">
        <Flex direction="column" justify="center" align="center" mb={24} gap={4}>
          <Title order={2}>{t('아이디(이메일)')}</Title>
          <Text align="center">
            <Trans
              i18nKey={
                '계정에 등록된 휴대폰 번호를 인증하시면 사용중인 계정의 이메일 주소를 알려드립니다.'
              }
              components={{ br: <br /> }}
            />
          </Text>
        </Flex>
        <PhoneVerificationForm onSend={() => setIsSend(true)} isSend={isSend} withCaptcha />

        {isSend && (
          <VerificationCodeInput
            onClickCompletes={onClickCompletes}
            styleForm={css({ marginTop: 24, flexWrap: 'wrap', button: { width: '100%' } })}
          />
        )}
      </Flex>
    </FormProvider>
  );
}
